import { useEffect, useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { Users, Clock, CalendarOff, RefreshCcw, BadgeDollarSign, ClipboardCheck, FileText } from 'lucide-react'
import { apiGet } from '../lib/api.js'
import { useMe } from '../lib/me.js'
import { canViewSalary } from '../lib/role.js'
import { EmployeeDrawerProvider } from './employee/EmployeeDrawer.jsx'
import { EmployeeFilterProvider } from './employee/EmployeeFilter.jsx'

// Horizontal tab shell for the พนักงาน / Employee-HR area (mirrors ChartLayout).
// The Recurring-Deduction and Salary tabs are HR/Admin-only — hidden from plain
// managers (the /api/hr/salary* + employee-recurring proxies also 403 them).
const TABS = [
  { to: 'list',      label: 'รายชื่อ',     icon: Users,           hint: 'ข้อมูลพนักงาน' },
  { to: 'clockin',   label: 'ลงเวลา',      icon: Clock,           hint: 'เข้า-ออกงาน · สาย · OT · เดินทาง' },
  { to: 'leave',     label: 'การลา',       icon: CalendarOff,     hint: 'โควตาและวันลารายเดือน', badge: 'leave' },
  { to: 'recurring', label: 'หักประจำ',    icon: RefreshCcw,      hint: 'รายการหักเงินแบบผ่อนชำระ', hrOnly: true },
  { to: 'salary',    label: 'เงินเดือน',   icon: BadgeDollarSign, hint: 'คำนวณและส่งออกเงินเดือน',  hrOnly: true },
  // Not hrOnly: a Manager runs rounds and reads the report. The form/rule builders inside
  // are HR-gated separately, and Xano refuses them to a plain Manager regardless.
  { to: 'evaluation', label: 'ประเมิน',    icon: ClipboardCheck,  hint: 'รอบประเมิน แบบฟอร์ม และผลคะแนน' },
  // LAST on purpose (moved 2026-08-30). เอกสาร holds หนังสือเตือน + ลาออก behind a
  // second tab row. ONE tab rather than two, because both are low-volume "a document
  // about a person" work rather than roster or payroll work — and it sits at the end
  // because it is the only tab that is not part of the monthly roster→payroll→ประเมิน
  // run everything to its left belongs to. Not hrOnly: a branch Manager legitimately
  // reads the ลาออก queue (they backfill the shift); the หนังสือเตือน tab inside
  // carries its own HR/GM/Admin gate, and the off-boarding answers are refused to
  // them by the middleware and by Xano.
  { to: 'docs',      label: 'เอกสาร',      icon: FileText,        hint: 'หนังสือเตือน และหนังสือแจ้งลาออก', badge: 'docs' },
]

/* Pending counts for the two tabs whose queue lives one row DOWN — การลา › คำขอลา
 * and เอกสาร › ลาออก. The sub-tab pill alone was not enough: a ใบลาออก sat unseen
 * because nobody opened เอกสาร to find the pill (2026-10-31). Same endpoints and the
 * same amber as LeaveLayout / DocsLayout, so the tab and the pill it opens agree.
 * Re-read on every navigation inside the section, so a decided request drops off
 * the moment you move on rather than at the next reload. */
function usePendingCounts() {
  const { pathname } = useLocation()
  const [counts, setCounts] = useState({ leave: 0, docs: 0 })
  useEffect(() => {
    let alive = true
    const read = (path, key) => apiGet(path, { status: 'pending' })
      .then((r) => { if (alive) setCounts((c) => ({ ...c, [key]: Number(r?.pending_count || 0) })) })
      .catch(() => {})
    read('/api/hr/leave-request', 'leave')
    read('/api/hr/resignation', 'docs')
    return () => { alive = false }
  }, [pathname])
  return counts
}

export default function EmployeeLayout() {
  const me = useMe()
  const hr = canViewSalary(me)
  const tabs = TABS.filter((t) => !t.hrOnly || hr)
  const pending = usePendingCounts()
  return (
    <div className="px-10 py-8">
      <div className="flex items-baseline justify-between mb-1">
        <h1 className="text-[22px] font-semibold text-slate-900 tracking-tight">พนักงาน</h1>
      </div>
      <p className="text-[13.5px] text-slate-500 mb-4">ข้อมูลพนักงาน การลงเวลา การลา และเงินเดือน</p>

      {/* Sub-tab nav — STICKY. These tabs are how you leave a page, and every one of
          them is a long scroller (a 380-row roster, a month of clock-ins, a payroll
          ledger): scrolled down, the way out was off-screen and the only route to the
          next tab was scrolling back to the top first.
          `top-0` is measured against <main> in Layout.jsx, which is the scroll
          container (`flex-1 overflow-auto`) — not the window. The background must be
          OPAQUE and must be the page's own #fafbfc (index.css body), or the rows
          passing underneath show through. `-mx-10 px-9` cancels the wrapper's px-10
          so the bar spans the full width, and the inner `-mx-1 px-1` is kept so the
          active underline and focus rings are not clipped by overflow-x-auto.
          z-30 sits above page content and below the drawers/dialogs (z-50). */}
      <div className="sticky top-0 z-30 -mx-10 px-9 bg-[#fafbfc] border-b border-slate-200 mb-6">
        <nav className="flex items-end gap-1 px-1 overflow-x-auto no-scrollbar">
          {tabs.map(({ to, label, icon: Icon, hint, badge }) => (
            <NavLink
              key={to}
              to={`/manager/employee/${to}`}
              title={hint}
              className={({ isActive }) =>
                `relative flex items-center gap-1.5 px-3 py-2.5 text-[14px] font-medium whitespace-nowrap transition-colors ${
                  isActive ? 'text-brand-700' : 'text-slate-500 hover:text-slate-900'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon size={14} strokeWidth={1.75} />
                  <span>{label}</span>
                  {badge && pending[badge] > 0 && (
                    <span className="ml-0.5 rounded-full bg-amber-100 text-amber-700 ring-1 ring-amber-200 px-1.5 text-[11px] font-semibold leading-[17px]">
                      {pending[badge]}
                    </span>
                  )}
                  {isActive && (
                    <span className="absolute left-2 right-2 -bottom-px h-[2px] bg-brand-600 rounded-full" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>
      </div>

      {/* One employee-detail drawer for the whole section — see EmployeeDrawer.jsx.
          Mounted here rather than per page so that every table under พนักงาน can open
          a person without each one wiring up its own editor, branches and role gate.
          The employee FILTER is shared the same way — pick a person on one tab and
          every tab follows (EmployeeFilter.jsx). */}
      <EmployeeFilterProvider>
        <EmployeeDrawerProvider>
          <Outlet />
        </EmployeeDrawerProvider>
      </EmployeeFilterProvider>
    </div>
  )
}
